import { useSyncExternalStore } from 'react'
import { fillSlots } from './final-check'

// R537: the till speaks one language at a time, Arabic first. The driver app's pattern
// (apps/driver-pwa: initialLang, a per-device key in localStorage, a toggle that names the
// other language); the words stay in the till's own `{ ar, en }` locale files, one per screen.
export type Lang = 'ar' | 'en'

export interface Pair {
  ar: string
  en: string
}

/** The till's per-device choice, as the driver app keeps `tkf-driver-lang`. */
export const LANG_KEY = 'tkf-cashier-lang'

/** Arabic is the till's default; a saved choice wins. */
export function initialLang(saved: string | null): Lang {
  return saved === 'en' ? 'en' : 'ar'
}

function readSaved(): string | null {
  try {
    return localStorage.getItem(LANG_KEY)
  } catch {
    return null
  }
}

const listeners = new Set<() => void>()

export function getLang(): Lang {
  return initialLang(readSaved())
}

export function setLang(next: Lang): void {
  try {
    localStorage.setItem(LANG_KEY, next)
  } catch {
    // Storage refused: nothing to remember on this device.
  }
  listeners.forEach((l) => l())
}

export function toggleLang(): void {
  setLang(getLang() === 'ar' ? 'en' : 'ar')
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  window.addEventListener('storage', listener)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', listener)
  }
}

/** The till's language; every screen re-renders when the toggle changes it. */
export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang, getLang)
}

/** One string of a pair, its {slots} filled by name. */
export function tr(pair: Pair, lang: Lang, slots?: Record<string, string | number>): string {
  if (!slots) return pair[lang]
  const asText: Record<string, string> = {}
  for (const [k, v] of Object.entries(slots)) asText[k] = String(v)
  return fillSlots(pair[lang], asText)
}

/**
 * Reads back which pair (and which slot values) an English string was built from, so a
 * string stored in English by code this bench does not edit is drawn in the till's language.
 * null when the string is none of them (a server's own message is shown as it came).
 */
export function matchEnglish<K extends string>(
  stored: string,
  pairs: Record<K, Pair>,
  keys: readonly K[],
): { key: K; slots: Record<string, string> } | null {
  for (const key of keys) {
    const template = pairs[key].en
    const names: string[] = []
    const pattern = template
      .split(/(\{\w+\})/)
      .map((part) => {
        const slot = /^\{(\w+)\}$/.exec(part)
        if (slot) {
          names.push(slot[1])
          return '(.+?)'
        }
        return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      })
      .join('')
    const m = new RegExp(`^${pattern}$`).exec(stored)
    if (m) {
      const slots: Record<string, string> = {}
      names.forEach((n, i) => {
        slots[n] = m[i + 1]
      })
      return { key, slots }
    }
  }
  return null
}
